/**
 * Pairs drill strategy (Phase 3, design §1) — pure ports of today's Pairs
 * branches plus the solve-card / usePairs helpers. BUG-35 fixed: every place
 * resolves the direction via `isTargetToSource` (a missing `direction` means
 * 'Source to Target').
 */
import type {
  DrillPairs,
  DrillQaSettings,
  StackPairTargetList,
} from 'src/types/components';
import type { DrillQaReplyMethod } from 'src/types/course/drill';
import { orderArray } from 'src/shared/utilities/array';
import { removeExtraSpaces } from 'src/shared/utilities/string';
import { isTypedAnswerCorrect } from 'src/modules/Step/views/StepView/utilities/typedAnswer';
import { escapeText } from './escapeText';
import { isTargetToSource } from './direction';
import type {
  DrillContext,
  DrillItemMap,
  DrillResultRowMap,
  DrillStrategy,
} from './types';

type PairsItem = DrillItemMap['Pairs'];
type QaSettings = DrillQaSettings['attributes'] | undefined;

// ---------------------------------------------------------------------------
// usePairs (results card)
// ---------------------------------------------------------------------------

/** usePairs `isTarget`. */
export function pairsIsTarget(qaSettings: QaSettings): boolean {
  return isTargetToSource(qaSettings);
}

/** usePairs `sourceSentence` / `targetSentence` / `correctAnswers` for one item. */
export function pairsSentences(item: PairsItem, qaSettings: QaSettings) {
  const isTarget = pairsIsTarget(qaSettings);
  const firstTarget = item?.pair_target_list?.[0]?.target ?? '';
  return {
    source: isTarget ? firstTarget : item?.source,
    target: !isTarget ? firstTarget : item?.source,
    correct: !isTarget
      ? (item?.pair_target_list as StackPairTargetList['attributes'][])?.map(
          (tl: StackPairTargetList['attributes']) => tl?.target,
        )
      : [item?.source],
  };
}

// ---------------------------------------------------------------------------
// Strategy
// ---------------------------------------------------------------------------

/** useStep `currentCorrectAnswer` (Pairs branch). */
function correctAnswer(item: PairsItem, ctx: DrillContext): string[] {
  const isSource = !isTargetToSource(ctx.drillType?.qa_settings);

  const answer = isSource
    ? item?.pair_target_list?.map((c: { target: string }) => c?.target ?? '')
    : item && 'source' in item
      ? [(item.source as string) ?? '']
      : [''];

  return answer as string[];
}

const errorRow: DrillResultRowMap['Pairs'] = {
  id: -1,
  source: undefined,
  correct: undefined,
  tries: [],
};

export const pairsStrategy: DrillStrategy<'Pairs'> = {
  kind: 'Pairs',
  speaksQuestion: true,
  speaksAnswer: true,
  errorRow,

  correctAnswer,

  isCorrect(answer, item, ctx) {
    if (answer === undefined || answer === null) return false;
    const correct = correctAnswer(item, ctx);
    return (
      Array.isArray(correct) &&
      isTypedAnswerCorrect(answer, correct, ctx.typingRules)
    );
  },

  // HTML members are rendered via v-html: text is escaped (BUG-21).
  feedbackAnswerHtml(answer) {
    return escapeText(answer);
  },

  feedbackCorrectAnswer(item, ctx) {
    const correct = correctAnswer(item, ctx);
    return escapeText(Array.isArray(correct) ? correct.join(', ') : correct);
  },

  feedbackSubtitle(questionText) {
    return escapeText(questionText);
  },

  // An array: the dialog renders it as text (no v-html).
  helpAnswer(item, ctx) {
    return correctAnswer(item, ctx);
  },

  spokenAnswer(answer) {
    return answer;
  },

  /** Pairs StepDrillCard `sentence` + watchEffect. */
  questionText(item, ctx) {
    const sentence = isTargetToSource(ctx.drillType?.qa_settings)
      ? (item?.pair_target_list?.[0]?.target ?? '')
      : (item?.['source'] ?? '');
    return sentence || '';
  },

  /** Pairs StepResultsCard `results` row (item found, id valid). */
  resultRow(item, id, tries, ctx) {
    const { source, correct } = pairsSentences(
      item,
      ctx.drillType?.qa_settings,
    );
    return { id, source, correct, tries };
  },
};

// ---------------------------------------------------------------------------
// Solve card helpers
// ---------------------------------------------------------------------------

function qaSettingsOf(drillType: DrillPairs['attributes'] | undefined) {
  return drillType?.qa_settings as DrillQaSettings['attributes'];
}

function isSourceOf(drillType: DrillPairs['attributes'] | undefined) {
  return !isTargetToSource(qaSettingsOf(drillType));
}

/** Pairs StepSolveCard `itemCorrect`. */
export function pairsItemCorrect(
  currentStackItem: PairsItem | undefined,
  drillType: DrillPairs['attributes'] | undefined,
) {
  return isSourceOf(drillType)
    ? currentStackItem?.pair_target_list?.[0]?.target
    : currentStackItem?.source;
}

/**
 * Pairs StepSolveCard `choices` computed (with `itemOptions`, evaluated
 * lazily as before) — same `orderArray(…, 'random')` calls in the same order.
 */
export function pairsChoices(
  orderedStackSource: PairsItem[],
  currentStackItem: PairsItem | undefined,
  drillType: DrillPairs['attributes'] | undefined,
) {
  const qaSettings = qaSettingsOf(drillType);
  const isSource = isSourceOf(drillType);
  const itemCorrect = pairsItemCorrect(currentStackItem, drillType);
  const itemOptions = () =>
    isSource
      ? orderedStackSource
          ?.map((c) => c?.pair_target_list?.[0]?.target)
          .filter((c) => c !== itemCorrect)
      : orderedStackSource
          ?.map((c) => c?.source)
          .filter((c) => c !== itemCorrect);
  const numberOfOptions = qaSettings?.number_of_options ?? 0;

  if (
    (qaSettings?.reply_method as DrillQaReplyMethod) === 'Clicking the Reply' &&
    (!currentStackItem || numberOfOptions < 2)
  ) {
    // BUG-36: no other candidate → only the correct choice (no `undefined` button)
    const distractor = orderArray(itemOptions(), 'random')?.[0];
    return distractor === undefined ? [itemCorrect] : [itemCorrect, distractor];
  }

  if (numberOfOptions === 0) return [];

  if (numberOfOptions === 1) return [itemCorrect];

  if ((orderedStackSource?.length ?? 0) === numberOfOptions)
    return orderArray([...itemOptions(), itemCorrect], 'random') ?? [];

  const options = orderArray(
    [...itemOptions().slice(0, numberOfOptions - 1), itemCorrect],
    'random',
  );

  return options;
}

/** Pairs StepSolveCard `scater` computed (aid buttons). */
export function pairsScatter(
  currentStackItem: PairsItem | undefined,
  drillType: DrillPairs['attributes'] | undefined,
) {
  if (!drillType?.typing_aids?.display_aid_buttons) return [];
  const scatter = isSourceOf(drillType)
    ? removeExtraSpaces(currentStackItem?.target_scatter as string)?.split(' ')
    : removeExtraSpaces(currentStackItem?.source_scatter as string)?.split(' ');

  return drillType?.typing_aids?.aid_buttons_random_order
    ? orderArray(scatter, 'random')
    : scatter;
}
